/**
 * Waiting Room UI Component
 * Displays connected players, ready status, and chat while waiting to start
 */

import type { PlayerInfo, ChatMessage, RoomInfo, RoomStatus } from '../../multiplayer/types';
import { PartyKitClient } from '../../multiplayer/PartyKitClient';

/** Waiting room configuration */
export interface WaitingRoomConfig {
  roomInfo: RoomInfo;
  client: PartyKitClient;
  currentUserId: string;
  onGameStart?: () => void;
  onLeave?: () => void;
  onError?: (error: string) => void;
}

/** Waiting Room Component */
export class WaitingRoom {
  private container: HTMLElement;
  private config: WaitingRoomConfig;
  private players: PlayerInfo[] = [];
  private hostId: string = '';
  private _roomStatus: RoomStatus = 'waiting';
  private chatMessages: ChatMessage[] = [];
  private isReady = false;

  constructor(config: WaitingRoomConfig) {
    this.config = config;
    this.container = document.createElement('div');
    this.container.className = 'waiting-room';
    
    this.setupClientCallbacks();
    this.render();
  }

  /** Get the container element */
  getElement(): HTMLElement {
    return this.container;
  }

  /** Setup PartyKit client callbacks */
  private setupClientCallbacks(): void {
    this.config.client.setCallbacks({
      onPlayerUpdate: (players, hostId, status) => {
        this.players = players;
        this.hostId = hostId;
        this._roomStatus = status;
        this.render();
      },
      onChatMessage: (message) => {
        this.chatMessages.push(message);
        // Keep only last 100 messages
        if (this.chatMessages.length > 100) {
          this.chatMessages = this.chatMessages.slice(-100);
        }
        this.renderChat();
        this.scrollChatToBottom();
      },
      onGameStarted: () => {
        this.config.onGameStart?.();
      },
      onError: (error) => {
        this.config.onError?.(error.message);
      },
      onPlayerJoined: (player) => {
        this.addSystemMessage(`${player.name} joined the room`);
      },
      onPlayerLeft: (_, playerName) => {
        this.addSystemMessage(`${playerName} left the room`);
      },
    });
  }

  /** Add a system message to chat */
  private addSystemMessage(content: string): void {
    this.chatMessages.push({
      id: `system-${Date.now()}`,
      senderId: 'system',
      senderName: 'System',
      content,
      timestamp: new Date().toISOString(),
    });
    this.renderChat();
    this.scrollChatToBottom();
  }

  /** Check if current user is the host */
  private isHost(): boolean {
    return this.config.currentUserId === this.hostId;
  }

  /** Check if all players are ready */
  private allPlayersReady(): boolean {
    return this.players.length >= 2 && this.players.every(p => p.isReady);
  }

  /** Get current user's player info */
  private getCurrentPlayer(): PlayerInfo | undefined {
    return this.players.find(p => p.id === this.config.currentUserId);
  }

  /** Render the component */
  private render(): void {
    const currentPlayer = this.getCurrentPlayer();
    this.isReady = currentPlayer?.isReady || false;

    this.container.innerHTML = `
      <div class="waiting-room-header">
        <h2>Waiting Room</h2>
        <div class="room-code-display">
          <span class="room-code-label">Room Code:</span>
          <span class="room-code-value" id="room-code">${this.config.roomInfo.code}</span>
          <button class="btn-icon" id="copy-code-btn" title="Copy room code">
            <span class="copy-icon">📋</span>
          </button>
        </div>
      </div>

      <div class="waiting-room-content">
        <div class="players-section">
          <h3>Players</h3>
          <div class="players-list">
            ${this.renderPlayers()}
          </div>
          <div class="waiting-info">
            ${this.players.length < 2 
              ? '<p class="waiting-text">Waiting for another player...</p>'
              : this.allPlayersReady()
                ? '<p class="ready-text">All players ready!</p>'
                : '<p class="waiting-text">Waiting for all players to ready up...</p>'
            }
          </div>
        </div>

        <div class="chat-section">
          <h3>Chat</h3>
          <div class="chat-messages" id="chat-messages">
            ${this.renderChatMessages()}
          </div>
          <div class="chat-input-container">
            <input 
              type="text" 
              id="chat-input" 
              placeholder="Type a message..." 
              maxlength="500"
              class="chat-input"
            />
            <button class="btn btn-primary" id="send-chat-btn">Send</button>
          </div>
        </div>
      </div>

      <div class="waiting-room-actions">
        <button class="btn btn-secondary" id="leave-room-btn">Leave Room</button>
        <button 
          class="btn ${this.isReady ? 'btn-ready' : 'btn-primary'}" 
          id="ready-btn"
        >
          ${this.isReady ? '✓ Ready' : 'Ready Up'}
        </button>
        ${this.isHost() ? `
          <button 
            class="btn btn-success" 
            id="start-game-btn"
            ${!this.allPlayersReady() ? 'disabled' : ''}
          >
            Start Game
          </button>
        ` : ''}
      </div>

      <div class="room-settings-display">
        <span>Board: ${this.config.roomInfo.settings.boardSize}×${this.config.roomInfo.settings.boardSize}</span>
        <span>•</span>
        <span>${this.config.roomInfo.settings.isPublic ? 'Public' : 'Private'} Room</span>
      </div>
    `;

    this.attachEventListeners();
  }

  /** Render players list */
  private renderPlayers(): string {
    if (this.players.length === 0) {
      return '<div class="no-players">No players connected</div>';
    }

    return this.players.map(player => {
      const isCurrentUser = player.id === this.config.currentUserId;
      const isHost = player.id === this.hostId;
      
      return `
        <div class="player-card ${player.isConnected ? '' : 'disconnected'} ${isCurrentUser ? 'current-user' : ''}">
          <div class="player-color" style="background-color: ${player.color}"></div>
          <div class="player-info">
            <span class="player-name">
              ${player.name}
              ${isCurrentUser ? ' (You)' : ''}
              ${isHost ? ' 👑' : ''}
            </span>
            <span class="player-status ${player.isReady ? 'ready' : 'not-ready'}">
              ${player.isConnected 
                ? (player.isReady ? '✓ Ready' : 'Not Ready')
                : 'Disconnected'
              }
            </span>
          </div>
        </div>
      `;
    }).join('');
  }

  /** Render chat messages */
  private renderChatMessages(): string {
    if (this.chatMessages.length === 0) {
      return '<div class="chat-empty">No messages yet</div>';
    }

    return this.chatMessages.map(msg => {
      const isSystem = msg.senderId === 'system';
      const isCurrentUser = msg.senderId === this.config.currentUserId;
      
      return `
        <div class="chat-message ${isSystem ? 'system' : ''} ${isCurrentUser ? 'own' : ''}">
          ${!isSystem ? `<span class="chat-sender">${msg.senderName}:</span>` : ''}
          <span class="chat-content">${this.escapeHtml(msg.content)}</span>
        </div>
      `;
    }).join('');
  }

  /** Render only the chat section */
  private renderChat(): void {
    const chatContainer = this.container.querySelector('#chat-messages');
    if (chatContainer) {
      chatContainer.innerHTML = this.renderChatMessages();
    }
  }

  /** Scroll chat to bottom */
  private scrollChatToBottom(): void {
    const chatContainer = this.container.querySelector('#chat-messages');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }

  /** Escape HTML to prevent XSS */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /** Attach event listeners */
  private attachEventListeners(): void {
    // Copy room code
    const copyBtn = this.container.querySelector('#copy-code-btn');
    copyBtn?.addEventListener('click', () => this.copyRoomCode());

    // Ready button
    const readyBtn = this.container.querySelector('#ready-btn');
    readyBtn?.addEventListener('click', () => {
      this.isReady = !this.isReady;
      this.config.client.sendReady(this.isReady);
    });

    // Start game button (host only)
    const startBtn = this.container.querySelector('#start-game-btn');
    startBtn?.addEventListener('click', () => {
      if (this.isHost() && this.allPlayersReady()) {
        this.config.client.sendStart();
      }
    });

    // Leave room button
    const leaveBtn = this.container.querySelector('#leave-room-btn');
    leaveBtn?.addEventListener('click', () => {
      this.config.client.sendLeave();
      this.config.onLeave?.();
    });

    // Chat input
    const chatInput = this.container.querySelector('#chat-input') as HTMLInputElement;
    const sendBtn = this.container.querySelector('#send-chat-btn');

    const sendMessage = () => {
      const content = chatInput.value.trim();
      if (content) {
        this.config.client.sendChatMessage(content);
        chatInput.value = '';
      }
    };

    sendBtn?.addEventListener('click', sendMessage);
    chatInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        sendMessage();
      }
    });
  }

  /** Copy room code to clipboard */
  private async copyRoomCode(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.config.roomInfo.code);
      
      // Show feedback
      const codeEl = this.container.querySelector('#room-code');
      if (codeEl) {
        const original = codeEl.textContent;
        codeEl.textContent = 'Copied!';
        setTimeout(() => {
          codeEl.textContent = original;
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to copy room code:', error);
    }
  }

  /** Update players list */
  updatePlayers(players: PlayerInfo[]): void {
    this.players = players;
    this.render();
  }

  /** Destroy the component */
  destroy(): void {
    this.container.innerHTML = '';
  }
}

/** Create a waiting room component */
export function createWaitingRoom(config: WaitingRoomConfig): WaitingRoom {
  return new WaitingRoom(config);
}
